interface Props {
  /**
   * Whether Apple Pay can actually run on this device/browser/session. When
   * false the button is still rendered (per design) but disabled.
   */
  applePayAvailable: boolean;
  /** Whether Google Pay can run here. Rendered disabled when false. */
  googlePayAvailable: boolean;
  /**
   * Global lock — kill-switch, in-flight payment, or stale amount. Disables
   * both buttons regardless of wallet availability.
   */
  disabled: boolean;
  onPay: (method: 'applepay' | 'googlepay') => void;
}

/**
 * "Wallet" payment group — Apple Pay / Google Pay shown side-by-side as the
 * tall pill buttons from the redesign. Availability is decided upstream
 * (native detection / Pay.com `canMakePayments`); a wallet that isn't usable
 * on the device is shown as a disabled button rather than hidden.
 */
export function ExpressCheckout({ applePayAvailable, googlePayAvailable, disabled, onPay }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[18px] font-semibold text-ink">Wallet</span>
      <div className="flex gap-2.5">
        <WalletButton
          label="Pay with Apple Pay"
          disabled={disabled || !applePayAvailable}
          unavailable={!applePayAvailable}
          onClick={() => onPay('applepay')}
        >
          <ApplePayLogo />
        </WalletButton>
        <WalletButton
          label="Pay with Google Pay"
          disabled={disabled || !googlePayAvailable}
          unavailable={!googlePayAvailable}
          onClick={() => onPay('googlepay')}
        >
          <GooglePayLogo />
        </WalletButton>
      </div>
    </div>
  );
}

interface WalletButtonProps {
  label: string;
  disabled: boolean;
  /** Disabled specifically because the wallet isn't usable here — adds a hint. */
  unavailable?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function WalletButton({ label, disabled, unavailable = false, onClick, children }: WalletButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      title={unavailable ? 'Not available on this device or browser' : undefined}
      className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[40px] border-2 border-stroke-soft bg-surface text-ink transition-all duration-150 hover:border-purple/40 hover:shadow-card active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}

function ApplePayLogo() {
  return (
    <svg role="img" aria-hidden="true" width="58" height="26" viewBox="0 0 58 26" className="fill-brand-apple">
      <path d="M11.7 7.04c-.66.78-1.71 1.4-2.76 1.32-.13-1.06.39-2.18 1.01-2.88.67-.8 1.81-1.38 2.72-1.42.11 1.1-.32 2.2-.97 2.98zm.96 1c-1.52-.09-2.82.87-3.55.87-.74 0-1.85-.82-3.04-.8-1.56.02-3.01.91-3.82 2.31-1.63 2.83-.43 7.02 1.17 9.31.78 1.14 1.7 2.4 2.92 2.36 1.17-.04 1.62-.76 3.03-.76s1.81.76 3.04.74c1.27-.02 2.06-1.14 2.84-2.29.89-1.31 1.26-2.59 1.28-2.65-.03-.01-2.45-.94-2.48-3.72-.02-2.33 1.91-3.45 1.99-3.5-1.09-1.6-2.79-1.79-3.38-1.82z" />
      <text
        x="20"
        y="18.5"
        fontSize="17"
        fontWeight="600"
        fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', sans-serif"
        letterSpacing="-0.4"
        className="fill-brand-apple"
      >
        Pay
      </text>
    </svg>
  );
}

function GooglePayLogo() {
  return (
    <svg role="img" aria-hidden="true" width="74" height="26" viewBox="0 0 74 26" fill="none">
      <g transform="translate(0 1)">
        <path
          className="fill-brand-google-blue"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-7.09z"
        />
        <path
          className="fill-brand-google-green"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          className="fill-brand-google-yellow"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        />
        <path
          className="fill-brand-google-red"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </g>
      <text
        x="30"
        y="18"
        fontSize="17"
        fontWeight="600"
        className="fill-brand-googlepay-text"
        fontFamily="'Roboto', 'Google Sans', system-ui, sans-serif"
        letterSpacing="-0.3"
      >
        Pay
      </text>
    </svg>
  );
}
